import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";

dayjs.extend(relativeTime);
dayjs.extend(utc);

/**
 * Parse a date-only string (e.g. "2026-04-28" or "2026-04-28T00:00:00.000Z")
 * in UTC mode so the browser's timezone offset never shifts the day.
 */
export function parseDate(dateString) {
  if (!dateString) return null;
  return dayjs.utc(dateString);
}

/**
 * The business timezone. Both instances (Firefleet and SwarmSystems) are US
 * Eastern, so this is not instance-forked. Mirrors BUSINESS_TZ in api/src/config.js
 * — if one moves, move both, or the board and its API disagree for a day.
 */
const BUSINESS_TZ = "America/New_York";

/**
 * Built once. Constructing an Intl.DateTimeFormat costs roughly 1000x what
 * formatting with an existing one does, and businessToday() sits on a
 * per-event render path (the events list asks three times per row). Only the
 * DATE being formatted varies, never the formatter, so hoisting is safe —
 * caching the RESULT would not be, since it changes at midnight Eastern.
 */
const BUSINESS_DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
  timeZone: BUSINESS_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * Today on the warehouse floor, as a UTC-midnight dayjs.
 *
 * Use this — NOT `dayjs.utc().startOf('day')` — whenever you compare against a
 * date-only API field. `dayjs.utc()` is the UTC calendar date, which from 8PM
 * Eastern (7PM in winter) is already TOMORROW: a shipment going out today reads
 * as overdue, and a show ending today reads as completed, for the last four hours
 * of every working evening.
 *
 * Returns UTC-midnight on purpose, so it compares cleanly against parseDate()
 * values, which are UTC-parsed for exactly the same never-shift-the-day reason.
 * The result is a calendar date wearing a UTC hat, not a moment in time.
 *
 * Intl carries the IANA zone database, so DST is handled for free — never
 * hand-roll a -5/-4 offset. Parts are assembled explicitly rather than leaning on
 * 'en-CA' happening to emit YYYY-MM-DD.
 */
export function businessToday() {
  const parts = BUSINESS_DATE_FORMAT.formatToParts(new Date());
  const get = (type) => parts.find((p) => p.type === type).value;
  return dayjs.utc(`${get("year")}-${get("month")}-${get("day")}`);
}

/**
 * Format a date-only value (event_date, end_date, rain_date) safely.
 * Accepts an optional dayjs format string (default "MMM D, YYYY").
 */
export function formatEventDate(dateString, fmt = "MMM D, YYYY") {
  const d = parseDate(dateString);
  return d ? d.format(fmt) : "";
}

/**
 * Parse a TIMESTAMPTZ value in local time (for created_at, dateofservice, etc.).
 * Unlike parseDate() which forces UTC, this respects the user's timezone so
 * "8 PM Eastern on March 31" displays as March 31, not April 1.
 */
export function parseTimestamp(dateString) {
  if (!dateString) return null;
  return dayjs(dateString);
}

/**
 * Format a TIMESTAMPTZ value in the user's local timezone.
 * Use for created_at, dateofservice, dateofuse, dateissued, etc.
 */
export function formatTimestamp(dateString, fmt = "MMM D, YYYY") {
  const d = parseTimestamp(dateString);
  return d ? d.format(fmt) : "";
}

/**
 * Format last seen with relative time for recent, full date for older.
 * Shows "2 hours ago" for <24 hours, "MMM D, YYYY" for older.
 */
export function formatLastSeen(dateString) {
  if (!dateString) return "";
  const date = dayjs(dateString);
  const now = dayjs();
  const hoursDiff = now.diff(date, "hour");

  if (hoursDiff < 24) {
    return date.fromNow();
  } else {
    return date.format("MMM D, YYYY");
  }
}

/**
 * Format last seen with time included for older dates.
 * Shows "2 hours ago" for <24 hours, "MMM D, YYYY at h:mm A" for older.
 */
export function formatLastSeenWithTime(dateString) {
  if (!dateString) return "";
  const date = dayjs(dateString);
  const now = dayjs();
  const hoursDiff = now.diff(date, "hour");

  if (hoursDiff < 24) {
    return date.fromNow();
  } else {
    return date.format("MMM D, YYYY [at] h:mm A");
  }
}

/**
 * Get the full formatted date for tooltips.
 */
export function getFullDateTime(dateString) {
  if (!dateString) return "";
  return dayjs(dateString).format("MMM D, YYYY [at] h:mm A");
}

/**
 * Add/subtract days from a 'YYYY-MM-DD' string in UTC, returning 'YYYY-MM-DD'.
 * Avoids dayjs/Date's local-timezone DST hazards. Used by the scheduler's
 * drag-extend and shift-merge logic where day boundaries must be exact.
 */
export function shiftDay(ymd, delta) {
  if (!ymd) return ymd;
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return dt.toISOString().slice(0, 10);
}

/**
 * Enumerate 'YYYY-MM-DD' days from start to end inclusive.
 * Returns [] if end < start.
 */
export function enumerateDays(start, end) {
  if (!start || !end || end < start) return [];
  const out = [];
  const [y, m, d] = start.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  while (true) {
    const ds = dt.toISOString().slice(0, 10);
    if (ds > end) break;
    out.push(ds);
    dt.setUTCDate(dt.getUTCDate() + 1);
  }
  return out;
}

/**
 * Derive display status for an event.
 * "completed" is not a DB value — it's derived from active + past show date.
 */
export function getDisplayStatus(event) {
  if (!event) return '';
  if (event.status === 'active') {
    const showEnd = parseDate(event.end_date || event.event_date);
    // businessToday(), not dayjs.utc(): a show ending TODAY was being displayed as
    // 'completed' from 8PM Eastern onward, while it was still in the air.
    if (showEnd && showEnd.isBefore(businessToday())) return 'completed';
  }
  return event.status;
}
